const PREVIEW_BASE = 'https://media.easy-peasy.ai/previews/cinematic';

function toSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[/°]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');
}

function previewUrl(category: string, label: string): string {
  return `${PREVIEW_BASE}/${category}/${toSlug(label)}.webp?v=5`;
}

export interface CinematicOption {
  label: string;
  promptText: string;
  previewUrl?: string;
}

export const CAMERA_OPTIONS: CinematicOption[] = [
  { label: 'Standard', promptText: '' },
  {
    label: 'Cinema Digital 35mm',
    promptText: 'shot on Cinema Digital 35mm',
    previewUrl: previewUrl('camera', 'Cinema Digital 35mm'),
  },
  {
    label: 'Cinema Digital 70mm',
    promptText: 'shot on Cinema Digital 70mm',
    previewUrl: previewUrl('camera', 'Cinema Digital 70mm'),
  },
  {
    label: 'Cinema 70mm Film',
    promptText: 'shot on Cinema 70mm Film',
    previewUrl: previewUrl('camera', 'Cinema 70mm Film'),
  },
  {
    label: 'Lo-Fi Toy Camera',
    promptText: 'shot on Lo-Fi Toy Camera',
    previewUrl: previewUrl('camera', 'Lo-Fi Toy Camera'),
  },
  {
    label: 'Camcorder',
    promptText: 'shot on Camcorder',
    previewUrl: previewUrl('camera', 'Camcorder'),
  },
  {
    label: '8mm Film',
    promptText: 'shot on 8mm Film',
    previewUrl: previewUrl('camera', '8mm Film'),
  },
  {
    label: '35mm Film',
    promptText: 'shot on 35mm Film',
    previewUrl: previewUrl('camera', '35mm Film'),
  },
  {
    label: 'Medium Format Film',
    promptText: 'shot on Medium Format Film',
    previewUrl: previewUrl('camera', 'Medium Format Film'),
  },
  {
    label: 'iPhone 15 Pro',
    promptText: 'shot on iPhone 15 Pro',
    previewUrl: previewUrl('camera', 'iPhone 15 Pro'),
  },
  {
    label: 'Polaroid',
    promptText: 'shot on Polaroid camera',
    previewUrl: previewUrl('camera', 'Polaroid'),
  },
  {
    label: 'Fujifilm Instax',
    promptText: 'shot on Fujifilm Instax',
    previewUrl: previewUrl('camera', 'Fujifilm Instax'),
  },
  {
    label: 'GoPro',
    promptText: 'shot on GoPro',
    previewUrl: previewUrl('camera', 'GoPro'),
  },
  {
    label: 'Drone Aerial',
    promptText: 'shot on drone, aerial photography',
    previewUrl: previewUrl('camera', 'Drone Aerial'),
  },
  {
    label: 'ARRI Alexa',
    promptText: 'shot on ARRI Alexa',
    previewUrl: previewUrl('camera', 'ARRI Alexa'),
  },
  {
    label: 'Canon EOS R5',
    promptText: 'shot on Canon EOS R5',
    previewUrl: previewUrl('camera', 'Canon EOS R5'),
  },
  {
    label: 'Leica M10',
    promptText: 'shot on Leica M10',
    previewUrl: previewUrl('camera', 'Leica M10'),
  },
  {
    label: 'Pinhole Camera',
    promptText: 'shot on pinhole camera',
    previewUrl: previewUrl('camera', 'Pinhole Camera'),
  },
  {
    label: 'CCTV Security Camera',
    promptText: 'shot on CCTV security camera',
    previewUrl: previewUrl('camera', 'CCTV Security Camera'),
  },
];

export interface CameraPreset {
  id: string;
  label: string;
  description: string;
  previewUrl?: string;
  camera: string;
  lens: string;
  focalLength: string;
  aperture: string;
}

/**
 * Curated camera setups for regular users. Each preset bundles
 * camera + lens + focal length + aperture so the user only makes one choice.
 * Power users can still reach the full 4-column picker via Advanced.
 */
const CAMERA_PRESET_PREVIEW_BASE =
  'https://media.easy-peasy.ai/previews/camera-preset';
function presetPreviewUrl(id: string): string {
  return `${CAMERA_PRESET_PREVIEW_BASE}/${id}.webp?v=4`;
}

export const CAMERA_PRESETS: CameraPreset[] = [
  {
    id: 'cinematic',
    label: 'Cinematic',
    description: '70mm anamorphic film look',
    camera: 'Cinema 70mm Film',
    lens: 'Anamorphic',
    focalLength: '35mm',
    aperture: 'f/2.8',
    previewUrl: presetPreviewUrl('cinematic'),
  },
  {
    id: 'studio-portrait',
    label: 'Studio Portrait',
    description: 'Medium format + 85mm at f/2.8',
    camera: 'Medium Format Film',
    lens: 'Medium Format Planar',
    focalLength: '85mm',
    aperture: 'f/2.8',
    previewUrl: presetPreviewUrl('studio-portrait'),
  },
  {
    id: 'documentary',
    label: 'Documentary',
    description: 'Clean 35mm prime handheld',
    camera: 'Cinema Digital 35mm',
    lens: 'Prime Lens',
    focalLength: '35mm',
    aperture: 'f/4',
    previewUrl: presetPreviewUrl('documentary'),
  },
  {
    id: 'classic-35mm',
    label: 'Classic 35mm',
    description: 'Grainy 35mm film still',
    camera: '35mm Film',
    lens: 'Vintage Aspherical Lens',
    focalLength: '50mm',
    aperture: 'f/2.8',
    previewUrl: presetPreviewUrl('classic-35mm'),
  },
  {
    id: 'phone-snap',
    label: 'Phone Snap',
    description: 'iPhone 15 Pro, casual',
    camera: 'iPhone 15 Pro',
    lens: 'Standard',
    focalLength: 'Standard',
    aperture: 'Standard',
    previewUrl: presetPreviewUrl('phone-snap'),
  },
  {
    id: 'vintage-8mm',
    label: 'Vintage 8mm',
    description: 'Nostalgic 8mm home movie',
    camera: '8mm Film',
    lens: 'Standard',
    focalLength: 'Standard',
    aperture: 'Standard',
    previewUrl: presetPreviewUrl('vintage-8mm'),
  },
  {
    id: 'instant',
    label: 'Instant Photo',
    description: 'Polaroid square frame',
    camera: 'Polaroid',
    lens: 'Standard',
    focalLength: 'Standard',
    aperture: 'Standard',
    previewUrl: presetPreviewUrl('instant'),
  },
  {
    id: 'aerial',
    label: 'Aerial',
    description: 'Drone, wide landscape',
    camera: 'Drone Aerial',
    lens: 'Standard',
    focalLength: 'Standard',
    aperture: 'Standard',
    previewUrl: presetPreviewUrl('aerial'),
  },
  {
    id: 'action',
    label: 'Action Cam',
    description: 'GoPro wide-angle',
    camera: 'GoPro',
    lens: 'Standard',
    focalLength: 'Standard',
    aperture: 'Standard',
    previewUrl: presetPreviewUrl('action'),
  },
];

/** Find a preset that matches the current camera+lens+focal+aperture combo. */
export function findMatchingPreset(
  camera: string,
  lens: string,
  focalLength: string,
  aperture: string,
): CameraPreset | undefined {
  return CAMERA_PRESETS.find(
    (p) =>
      p.camera === camera &&
      p.lens === lens &&
      p.focalLength === focalLength &&
      p.aperture === aperture,
  );
}

/** Cameras with fixed optics — lens, focal length, and aperture don't apply. */
export const FIXED_OPTICS_CAMERAS = new Set([
  'Lo-Fi Toy Camera',
  'Camcorder',
  'iPhone 15 Pro',
  'Polaroid',
  'Fujifilm Instax',
  'GoPro',
  'Drone Aerial',
  'Disposable Camera',
  'Holga',
  'Lomo LC-A',
  'Pinhole Camera',
  'CCTV Security Camera',
]);

export const LENS_OPTIONS: CinematicOption[] = [
  { label: 'Standard', promptText: '' },
  {
    label: 'Anamorphic',
    promptText: 'Anamorphic lens',
    previewUrl: previewUrl('lens', 'Anamorphic'),
  },
  {
    label: 'Vintage Aspherical Lens',
    promptText: 'Vintage Aspherical Lens',
    previewUrl: previewUrl('lens', 'Vintage Aspherical Lens'),
  },
  {
    label: 'Prime Lens',
    promptText: 'Prime Lens',
    previewUrl: previewUrl('lens', 'Prime Lens'),
  },
  {
    label: 'Medium Format',
    promptText: 'Medium Format lens',
    previewUrl: previewUrl('lens', 'Medium Format'),
  },
  {
    label: 'DSLR Full Frame',
    promptText: 'DSLR Full Frame',
    previewUrl: previewUrl('lens', 'DSLR Full Frame'),
  },
  {
    label: 'Medium Format Planar',
    promptText: 'Medium Format Planar',
    previewUrl: previewUrl('lens', 'Medium Format Planar'),
  },
  {
    label: 'Fisheye',
    promptText: 'Fisheye lens',
    previewUrl: previewUrl('lens', 'Fisheye'),
  },
  {
    label: 'Tilt-Shift',
    promptText: 'Tilt-Shift lens',
    previewUrl: previewUrl('lens', 'Tilt-Shift'),
  },
  {
    label: 'Macro',
    promptText: 'Macro lens',
    previewUrl: previewUrl('lens', 'Macro'),
  },
  {
    label: 'Telephoto',
    promptText: 'Telephoto lens',
    previewUrl: previewUrl('lens', 'Telephoto'),
  },
  {
    label: 'Wide-Angle',
    promptText: 'Wide-Angle lens',
    previewUrl: previewUrl('lens', 'Wide-Angle'),
  },
  {
    label: 'Soft Focus',
    promptText: 'Soft Focus lens',
    previewUrl: previewUrl('lens', 'Soft Focus'),
  },
  {
    label: 'Petzval',
    promptText: 'Petzval lens',
    previewUrl: previewUrl('lens', 'Petzval'),
  },
];

export const FOCAL_LENGTH_OPTIONS: CinematicOption[] = [
  { label: 'Standard', promptText: '' },
  {
    label: '14mm',
    promptText: '14mm focal length',
    previewUrl: previewUrl('focal-length', '14mm'),
  },
  {
    label: '24mm',
    promptText: '24mm focal length',
    previewUrl: previewUrl('focal-length', '24mm'),
  },
  {
    label: '35mm',
    promptText: '35mm focal length',
    previewUrl: previewUrl('focal-length', '35mm'),
  },
  {
    label: '50mm',
    promptText: '50mm focal length',
    previewUrl: previewUrl('focal-length', '50mm'),
  },
  {
    label: '85mm',
    promptText: '85mm focal length',
    previewUrl: previewUrl('focal-length', '85mm'),
  },
  {
    label: '135mm',
    promptText: '135mm focal length',
    previewUrl: previewUrl('focal-length', '135mm'),
  },
  {
    label: '200mm',
    promptText: '200mm focal length',
    previewUrl: previewUrl('focal-length', '200mm'),
  },
];

export const APERTURE_OPTIONS: CinematicOption[] = [
  { label: 'Standard', promptText: '' },
  {
    label: 'f/1.2',
    promptText: 'f/1.2 aperture',
    previewUrl: previewUrl('aperture', 'f/1.2'),
  },
  {
    label: 'f/1.4',
    promptText: 'f/1.4 aperture',
    previewUrl: previewUrl('aperture', 'f/1.4'),
  },
  {
    label: 'f/2',
    promptText: 'f/2 aperture',
    previewUrl: previewUrl('aperture', 'f/2'),
  },
  {
    label: 'f/2.8',
    promptText: 'f/2.8 aperture',
    previewUrl: previewUrl('aperture', 'f/2.8'),
  },
  {
    label: 'f/4',
    promptText: 'f/4 aperture',
    previewUrl: previewUrl('aperture', 'f/4'),
  },
  {
    label: 'f/5.6',
    promptText: 'f/5.6 aperture',
    previewUrl: previewUrl('aperture', 'f/5.6'),
  },
  {
    label: 'f/8',
    promptText: 'f/8 aperture',
    previewUrl: previewUrl('aperture', 'f/8'),
  },
  {
    label: 'f/11',
    promptText: 'f/11 aperture',
    previewUrl: previewUrl('aperture', 'f/11'),
  },
  {
    label: 'f/16',
    promptText: 'f/16 aperture',
    previewUrl: previewUrl('aperture', 'f/16'),
  },
];

export const LIGHTING_OPTIONS: CinematicOption[] = [
  { label: 'None', promptText: '' },
  {
    label: 'Golden Hour',
    promptText: 'golden hour lighting',
    previewUrl: previewUrl('lighting', 'Golden Hour'),
  },
  {
    label: 'Rembrandt Lighting',
    promptText: 'Rembrandt lighting',
    previewUrl: previewUrl('lighting', 'Rembrandt Lighting'),
  },
  {
    label: 'Butterfly Lighting',
    promptText: 'butterfly lighting',
    previewUrl: previewUrl('lighting', 'Butterfly Lighting'),
  },
  {
    label: 'Loop Lighting',
    promptText: 'loop lighting',
    previewUrl: previewUrl('lighting', 'Loop Lighting'),
  },
  {
    label: 'Split Lighting',
    promptText: 'split lighting',
    previewUrl: previewUrl('lighting', 'Split Lighting'),
  },
  {
    label: 'Broad Lighting',
    promptText: 'broad lighting',
    previewUrl: previewUrl('lighting', 'Broad Lighting'),
  },
  {
    label: 'Short Lighting',
    promptText: 'short lighting',
    previewUrl: previewUrl('lighting', 'Short Lighting'),
  },
  {
    label: 'High Key',
    promptText: 'high key lighting',
    previewUrl: previewUrl('lighting', 'High Key'),
  },
  {
    label: 'Low Key',
    promptText: 'low key lighting',
    previewUrl: previewUrl('lighting', 'Low Key'),
  },
  {
    label: 'Rim Lighting',
    promptText: 'rim lighting',
    previewUrl: previewUrl('lighting', 'Rim Lighting'),
  },
  {
    label: 'Backlight',
    promptText: 'backlit',
    previewUrl: previewUrl('lighting', 'Backlight'),
  },
  {
    label: 'Hard Sunlight',
    promptText: 'hard sunlight',
    previewUrl: previewUrl('lighting', 'Hard Sunlight'),
  },
  {
    label: 'Blue Hour',
    promptText: 'blue hour lighting',
    previewUrl: previewUrl('lighting', 'Blue Hour'),
  },
  {
    label: 'Candlelight',
    promptText: 'candlelight',
    previewUrl: previewUrl('lighting', 'Candlelight'),
  },
  {
    label: 'Moonlight',
    promptText: 'moonlight',
    previewUrl: previewUrl('lighting', 'Moonlight'),
  },
  {
    label: 'Stage Lighting',
    promptText: 'stage lighting',
    previewUrl: previewUrl('lighting', 'Stage Lighting'),
  },
  {
    label: 'Volumetric Lighting',
    promptText: 'volumetric lighting',
    previewUrl: previewUrl('lighting', 'Volumetric Lighting'),
  },
  {
    label: 'Silhouette',
    promptText: 'silhouette lighting',
    previewUrl: previewUrl('lighting', 'Silhouette'),
  },
  {
    label: 'Neon Lighting',
    promptText: 'neon lighting',
    previewUrl: previewUrl('lighting', 'Neon Lighting'),
  },
  {
    label: 'Film Noir',
    promptText: 'film noir lighting',
    previewUrl: previewUrl('lighting', 'Film Noir'),
  },
  {
    label: 'Chiaroscuro',
    promptText: 'chiaroscuro lighting',
    previewUrl: previewUrl('lighting', 'Chiaroscuro'),
  },
  {
    label: 'Ring Light',
    promptText: 'ring light',
    previewUrl: previewUrl('lighting', 'Ring Light'),
  },
  {
    label: 'God Rays',
    promptText: 'god rays lighting',
    previewUrl: previewUrl('lighting', 'God Rays'),
  },
  {
    label: 'Overcast Diffused',
    promptText: 'overcast diffused lighting',
    previewUrl: previewUrl('lighting', 'Overcast Diffused'),
  },
  {
    label: 'Studio Strobe',
    promptText: 'studio strobe lighting',
    previewUrl: previewUrl('lighting', 'Studio Strobe'),
  },
  {
    label: 'Softbox',
    promptText: 'softbox lighting',
    previewUrl: previewUrl('lighting', 'Softbox'),
  },
];

export const SHOT_TYPE_OPTIONS: CinematicOption[] = [
  { label: 'None', promptText: '' },
  {
    label: 'Extreme Close-Up - Front',
    promptText: 'extreme close-up shot, front view',
    previewUrl: previewUrl('shot-type', 'Extreme Close-Up - Front'),
  },
  {
    label: 'Extreme Close-Up - 45°',
    promptText: 'extreme close-up shot, 45 degree angle',
    previewUrl: previewUrl('shot-type', 'Extreme Close-Up - 45°'),
  },
  {
    label: 'Extreme Close-Up - Profile',
    promptText: 'extreme close-up shot, profile view',
    previewUrl: previewUrl('shot-type', 'Extreme Close-Up - Profile'),
  },
  {
    label: 'Close-Up - Front',
    promptText: 'close-up shot, front view',
    previewUrl: previewUrl('shot-type', 'Close-Up - Front'),
  },
  {
    label: 'Close-Up - 45°',
    promptText: 'close-up shot, 45 degree angle',
    previewUrl: previewUrl('shot-type', 'Close-Up - 45°'),
  },
  {
    label: 'Close-Up - Profile',
    promptText: 'close-up shot, profile view',
    previewUrl: previewUrl('shot-type', 'Close-Up - Profile'),
  },
  {
    label: 'Medium Shot - Front',
    promptText: 'medium shot, front view',
    previewUrl: previewUrl('shot-type', 'Medium Shot - Front'),
  },
  {
    label: 'Medium Shot - 45°',
    promptText: 'medium shot, 45 degree angle',
    previewUrl: previewUrl('shot-type', 'Medium Shot - 45°'),
  },
  {
    label: 'Medium Shot - Profile',
    promptText: 'medium shot, profile view',
    previewUrl: previewUrl('shot-type', 'Medium Shot - Profile'),
  },
  {
    label: 'Three Quarter - Front',
    promptText: 'three-quarter shot, front view',
    previewUrl: previewUrl('shot-type', 'Three Quarter - Front'),
  },
  {
    label: 'Three Quarter - 45°',
    promptText: 'three-quarter shot, 45 degree angle',
    previewUrl: previewUrl('shot-type', 'Three Quarter - 45°'),
  },
  {
    label: 'Three Quarter - Profile',
    promptText: 'three-quarter shot, profile view',
    previewUrl: previewUrl('shot-type', 'Three Quarter - Profile'),
  },
  {
    label: 'Long Shot - Front',
    promptText: 'long shot, front view',
    previewUrl: previewUrl('shot-type', 'Long Shot - Front'),
  },
  {
    label: 'Long Shot - Profile',
    promptText: 'long shot, profile view',
    previewUrl: previewUrl('shot-type', 'Long Shot - Profile'),
  },
  {
    label: 'Wide Shot - Front',
    promptText: 'wide shot, front view',
    previewUrl: previewUrl('shot-type', 'Wide Shot - Front'),
  },
  {
    label: 'Wide Shot - 45°',
    promptText: 'wide shot, 45 degree angle',
    previewUrl: previewUrl('shot-type', 'Wide Shot - 45°'),
  },
  {
    label: "Bird's Eye View",
    promptText: "bird's eye view shot",
    previewUrl: previewUrl('shot-type', "Bird's Eye View"),
  },
  {
    label: "Worm's Eye View",
    promptText: "worm's eye view shot",
    previewUrl: previewUrl('shot-type', "Worm's Eye View"),
  },
  {
    label: 'Dutch Angle',
    promptText: 'dutch angle shot',
    previewUrl: previewUrl('shot-type', 'Dutch Angle'),
  },
  {
    label: 'Low Angle',
    promptText: 'low angle shot',
    previewUrl: previewUrl('shot-type', 'Low Angle'),
  },
  {
    label: 'High Angle',
    promptText: 'high angle shot',
    previewUrl: previewUrl('shot-type', 'High Angle'),
  },
  {
    label: 'Over-the-Shoulder',
    promptText: 'over-the-shoulder shot',
    previewUrl: previewUrl('shot-type', 'Over-the-Shoulder'),
  },
  {
    label: 'POV First Person',
    promptText: 'POV first person perspective shot',
    previewUrl: previewUrl('shot-type', 'POV First Person'),
  },
];

export const FILM_STOCK_OPTIONS: CinematicOption[] = [
  { label: 'None', promptText: '' },
  {
    label: 'Kodak Portra 400',
    promptText: 'Kodak Portra 400 film stock',
    previewUrl: previewUrl('film-stock', 'Kodak Portra 400'),
  },
  {
    label: 'Kodak Portra 160',
    promptText: 'Kodak Portra 160 film stock',
    previewUrl: previewUrl('film-stock', 'Kodak Portra 160'),
  },
  {
    label: 'Kodak Ektar 100',
    promptText: 'Kodak Ektar 100 film stock',
    previewUrl: previewUrl('film-stock', 'Kodak Ektar 100'),
  },
  {
    label: 'Fujifilm Velvia 50',
    promptText: 'Fujifilm Velvia 50 film stock',
    previewUrl: previewUrl('film-stock', 'Fujifilm Velvia 50'),
  },
  {
    label: 'Fujifilm Pro 400H',
    promptText: 'Fujifilm Pro 400H film stock',
    previewUrl: previewUrl('film-stock', 'Fujifilm Pro 400H'),
  },
  {
    label: 'CineStill 800T',
    promptText: 'CineStill 800T film stock',
    previewUrl: previewUrl('film-stock', 'CineStill 800T'),
  },
  {
    label: 'Kodak Gold 200',
    promptText: 'Kodak Gold 200 film stock',
    previewUrl: previewUrl('film-stock', 'Kodak Gold 200'),
  },
  {
    label: 'Kodak Tri-X 400',
    promptText: 'Kodak Tri-X 400 black and white film stock',
    previewUrl: previewUrl('film-stock', 'Kodak Tri-X 400'),
  },
  {
    label: 'Ilford HP5 Plus',
    promptText: 'Ilford HP5 Plus black and white film stock',
    previewUrl: previewUrl('film-stock', 'Ilford HP5 Plus'),
  },
  {
    label: 'Ilford Delta 3200',
    promptText: 'Ilford Delta 3200 black and white film stock',
    previewUrl: previewUrl('film-stock', 'Ilford Delta 3200'),
  },
  {
    label: 'Kodak Ektachrome E100',
    promptText: 'Kodak Ektachrome E100 slide film stock',
    previewUrl: previewUrl('film-stock', 'Kodak Ektachrome E100'),
  },
  {
    label: 'Lomography 800',
    promptText: 'Lomography 800 film stock',
    previewUrl: previewUrl('film-stock', 'Lomography 800'),
  },
];

export interface CinematicState {
  camera: string;
  lens: string;
  focalLength: string;
  aperture: string;
  lighting: string;
  shotType: string;
  filmStock: string;
}

export const DEFAULT_CINEMATIC_STATE: CinematicState = {
  camera: 'Standard',
  lens: 'Standard',
  focalLength: 'Standard',
  aperture: 'Standard',
  lighting: 'None',
  shotType: 'None',
  filmStock: 'None',
};

export function buildCinematicPrompt(state: CinematicState): string {
  const parts: string[] = [];

  const findPromptText = (options: CinematicOption[], label: string) =>
    options.find((o) => o.label === label)?.promptText || '';

  const camera = findPromptText(CAMERA_OPTIONS, state.camera);
  const lens = findPromptText(LENS_OPTIONS, state.lens);
  const focal = findPromptText(FOCAL_LENGTH_OPTIONS, state.focalLength);
  const aperture = findPromptText(APERTURE_OPTIONS, state.aperture);
  const lighting = findPromptText(LIGHTING_OPTIONS, state.lighting);
  const shot = findPromptText(SHOT_TYPE_OPTIONS, state.shotType);
  const filmStock = findPromptText(FILM_STOCK_OPTIONS, state.filmStock);

  if (camera) parts.push(camera);
  if (lens) parts.push(lens);
  if (focal) parts.push(focal);
  if (aperture) parts.push(aperture);
  if (lighting) parts.push(lighting);
  if (shot) parts.push(shot);
  if (filmStock) parts.push(filmStock);

  return parts.length > 0 ? ', ' + parts.join(', ') : '';
}
